import {
  MDXEditor,
  diffSourcePlugin,
  directivesPlugin,
  frontmatterPlugin,
  headingsPlugin,
  linkDialogPlugin,
  linkPlugin,
  listsPlugin,
  markdownShortcutPlugin,
  quotePlugin,
  tablePlugin,
  thematicBreakPlugin,
  toolbarPlugin,
  type MDXEditorMethods,
} from '@mdxeditor/editor'
import {
  getBlockTitle,
  parseLegal,
  type DocumentType,
  type LintDiagnostic,
  type Variables,
} from '@westhetics/legal'
import { LegalDocument } from '@westhetics/legal/react'
import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { directiveDescriptors } from './directives'
import { iconComponentFor } from './icons'
import { insertRequiredBlock } from './required-blocks'
import { computeStats, type DocumentStats } from './stats'
import { Toolbar, type AvailableVariable } from './toolbar'
import { underlinePlugin } from './underline'

/**
 * O editor de documentos jurídicos da Westhetics.
 *
 * - WYSIWYG fiel ao Markdown (MDXEditor/Lexical sobre a mesma árvore mdast do renderizador).
 * - Os blocos do dialeto aparecem no editor como aparecem no documento.
 * - Modo fonte (CodeMirror) e diff contra a versão anterior — para "cláusula atualizada".
 * - Pré-visualização ao lado, renderizada por `@westhetics/legal`: o que se vê é o que o paciente lê,
 *   na largura inteira ou na de um celular (375px).
 * - Avisos de lint em tempo real (bloco obrigatório ausente, variável sem valor, diretiva fora do
 *   dialeto); o bloco obrigatório que falta se insere dali mesmo.
 * - Estatísticas: palavras, tempo de leitura, seções, cláusulas, variáveis.
 */

export interface MarkdownEditorProps {
  /** Markdown inicial. Para trocar depois de montado, use `ref.setMarkdown`. */
  markdown: string
  onChange?: (markdown: string) => void
  /** Variáveis que quem escreve pode inserir. */
  variables?: AvailableVariable[]
  /** Valores de exemplo para a pré-visualização (`{{patient.name}}` → "Amanda Lima"). */
  sampleValues?: Variables
  /** Versão anterior do documento — habilita o modo diff. */
  previousVersion?: string
  /**
   * Regras de lint por tipo (o termo de consentimento exige `:::summary`, `:::authorization`
   * e `:::risks`).
   */
  documentType?: DocumentType
  /** Confere cada variável contra o catálogo do `@westhetics/legal`: caminho fora dele vira erro. */
  checkCatalog?: boolean
  /** Rótulo da pré-visualização. Padrão: "Como o paciente vai ler". */
  previewLabel?: string
  /** Mostra a pré-visualização ao lado. Padrão: sim. */
  preview?: boolean
  readOnly?: boolean
  placeholder?: string
  /** Intervalo do onChange debounced, em ms. Padrão 400. */
  debounce?: number
  className?: string
}

export interface MarkdownEditorRef {
  getMarkdown: () => string
  setMarkdown: (md: string) => void
  insertMarkdown: (md: string) => void
  focus: () => void
}

export function MarkdownEditor({
  markdown,
  onChange,
  variables = [],
  sampleValues,
  previousVersion,
  documentType,
  checkCatalog = false,
  previewLabel = 'Como o paciente vai ler',
  preview = true,
  readOnly = false,
  placeholder = 'Escreva o documento. Comece pelo resumo em linguagem clara.',
  debounce = 400,
  className,
  ref,
}: MarkdownEditorProps & { ref?: React.Ref<MarkdownEditorRef> }) {
  const editorRef = useRef<MDXEditorMethods>(null)
  const [current, setCurrent] = useState(markdown)
  const [phonePreview, setPhonePreview] = useState(false)
  const timer = useRef<ReturnType<typeof setTimeout> | null>(null)

  const handleChange = useCallback(
    (md: string, initialNormalization: boolean) => {
      setCurrent(md)
      if (initialNormalization || !onChange) return
      if (timer.current) clearTimeout(timer.current)
      timer.current = setTimeout(() => onChange(md), debounce)
    },
    [onChange, debounce],
  )

  useEffect(
    () => () => {
      if (timer.current) clearTimeout(timer.current)
    },
    [],
  )

  useEffect(() => {
    if (typeof ref === 'function') {
      ref({
        getMarkdown: () => editorRef.current?.getMarkdown() ?? '',
        setMarkdown: (md) => editorRef.current?.setMarkdown(md),
        insertMarkdown: (md) => editorRef.current?.insertMarkdown(md),
        focus: () => editorRef.current?.focus(),
      })
    } else if (ref) {
      ref.current = {
        getMarkdown: () => editorRef.current?.getMarkdown() ?? '',
        setMarkdown: (md) => editorRef.current?.setMarkdown(md),
        insertMarkdown: (md) => editorRef.current?.insertMarkdown(md),
        focus: () => editorRef.current?.focus(),
      }
    }
  }, [ref])

  const insertMarkdown = useCallback((md: string) => {
    editorRef.current?.insertMarkdown(md)
    editorRef.current?.focus()
  }, [])

  // O bloco obrigatório que o aviso cobra entra no lugar certo do documento (`insertRequiredBlock`).
  // `setMarkdown` do MDXEditor não dispara o onChange dele: os avisos e o salvamento vêm daqui.
  const insertBlock = useCallback(
    (name: string) => {
      const editor = editorRef.current
      if (!editor) return
      const md = insertRequiredBlock(editor.getMarkdown(), name)
      editor.setMarkdown(md)
      handleChange(md, false)
    },
    [handleChange],
  )

  const plugins = useMemo(
    () => [
      headingsPlugin({ allowedHeadingLevels: [1, 2, 3, 4] }),
      listsPlugin(),
      quotePlugin(),
      thematicBreakPlugin(),
      tablePlugin(),
      linkPlugin(),
      linkDialogPlugin(),
      frontmatterPlugin(),
      directivesPlugin({
        directiveDescriptors,
        escapeUnknownTextDirectives: true,
      }),
      underlinePlugin(),
      diffSourcePlugin({ viewMode: 'rich-text', diffMarkdown: previousVersion ?? '' }),
      markdownShortcutPlugin(),
      toolbarPlugin({
        toolbarClassName: 'wx-ed-toolbar',
        toolbarContents: () => (
          <Toolbar
            variables={variables}
            insertMarkdown={insertMarkdown}
            withDiff={Boolean(previousVersion)}
          />
        ),
      }),
    ],
    // A barra e o diff só mudam quando estas entradas mudam; o MDXEditor lê plugins uma vez.
    [previousVersion, variables, insertMarkdown],
  )

  const analysis = useMemo(() => {
    const md = documentType ? injectDocumentType(current, documentType) : current
    const doc = parseLegal(md, { variables: sampleValues, catalog: checkCatalog })
    return { diagnostics: doc.diagnostics, stats: computeStats(current), md }
  }, [current, documentType, sampleValues, checkCatalog])

  return (
    <div
      className={['wx-editor', preview ? 'wx-editor--with-preview' : '', className ?? '']
        .filter(Boolean)
        .join(' ')}
      data-slot="markdown-editor"
    >
      <div className="wx-editor-panel">
        <MDXEditor
          ref={editorRef}
          markdown={markdown}
          onChange={handleChange}
          plugins={plugins}
          readOnly={readOnly}
          placeholder={placeholder}
          contentEditableClassName="wx-ed-content"
          className="wx-ed"
          suppressHtmlProcessing
          translation={translate}
          iconComponentFor={iconComponentFor}
        />
        <EditorFooter
          stats={analysis.stats}
          diagnostics={analysis.diagnostics}
          onInsertBlock={readOnly ? undefined : insertBlock}
        />
      </div>
      {preview ? (
        <aside className="wx-editor-preview" aria-label="Pré-visualização do documento">
          <div className="wx-editor-preview-head">
            <div className="wx-editor-preview-label">{previewLabel}</div>
            <div
              className="wx-editor-preview-modes"
              role="group"
              aria-label="Largura da pré-visualização"
            >
              <button
                type="button"
                aria-pressed={!phonePreview}
                onClick={() => setPhonePreview(false)}
              >
                Largura inteira
              </button>
              <button
                type="button"
                aria-pressed={phonePreview}
                onClick={() => setPhonePreview(true)}
              >
                Celular · 375px
              </button>
            </div>
          </div>
          <div
            className={
              phonePreview
                ? 'wx-editor-preview-frame wx-editor-preview-frame--phone'
                : 'wx-editor-preview-frame'
            }
          >
            <LegalDocument markdown={analysis.md} variables={sampleValues} />
          </div>
        </aside>
      ) : null}
    </div>
  )
}

function injectDocumentType(md: string, documentType: DocumentType) {
  if (/^---\n[\s\S]*?\n---/.test(md)) {
    if (/^type:/m.test(md)) return md
    return md.replace(/^---\n/, `---\ntype: ${documentType}\n`)
  }
  return `---\ntype: ${documentType}\n---\n\n${md}`
}

function EditorFooter({
  stats,
  diagnostics,
  onInsertBlock,
}: {
  stats: DocumentStats
  diagnostics: LintDiagnostic[]
  /** Sem isso (editor só de leitura), o aviso de bloco ausente não oferece inserir. */
  onInsertBlock?: (name: string) => void
}) {
  const errors = diagnostics.filter((d) => d.severity === 'error')
  const warnings = diagnostics.filter((d) => d.severity === 'warning')
  const action = (d: LintDiagnostic) => {
    if (!onInsertBlock || d.code !== 'missing_required_block' || !d.block) return null
    const name = d.block
    return (
      <button
        type="button"
        className="wx-ed-diagnostic-action"
        aria-label={`Inserir o bloco ${getBlockTitle(name) ?? name}`}
        onClick={() => onInsertBlock(name)}
      >
        Inserir o bloco
      </button>
    )
  }
  return (
    <div className="wx-ed-footer">
      <div className="wx-ed-stats" aria-label="Estatísticas do documento">
        <span>{stats.words} palavras</span>
        <span>{stats.readingMinutes} min de leitura</span>
        <span>
          {stats.sections} {stats.sections === 1 ? 'seção' : 'seções'} · {stats.clauses}{' '}
          {stats.clauses === 1 ? 'cláusula' : 'cláusulas'}
        </span>
        <span>{stats.variables} variáveis</span>
      </div>
      {diagnostics.length ? (
        <ul className="wx-ed-diagnostics" aria-live="polite">
          {errors.map((d, i) => (
            <li key={`e${i}`} className="wx-ed-diagnostic wx-ed-diagnostic--error">
              {d.message}
              {d.line ? ` (linha ${d.line})` : ''}
              {action(d)}
            </li>
          ))}
          {warnings.map((d, i) => (
            <li key={`w${i}`} className="wx-ed-diagnostic">
              {d.message}
              {d.line ? ` (linha ${d.line})` : ''}
            </li>
          ))}
        </ul>
      ) : (
        <div className="wx-ed-diagnostic wx-ed-diagnostic--ok">
          Nenhum aviso. O documento está dentro do dialeto.
        </div>
      )}
    </div>
  )
}

/** Tradução mínima da interface do MDXEditor para a voz da casa. */
function translate(
  key: string,
  defaultValue: string,
  interpolations?: Record<string, unknown>,
): string {
  const dictionary: Record<string, string> = {
    'toolbar.undo': 'Desfazer',
    'toolbar.redo': 'Refazer',
    'toolbar.bold': 'Negrito',
    'toolbar.italic': 'Itálico',
    'toolbar.underline': 'Sublinhado',
    'toolbar.strikethrough': 'Riscado',
    'toolbar.removeBold': 'Remover negrito',
    'toolbar.removeItalic': 'Remover itálico',
    'toolbar.removeUnderline': 'Remover sublinhado',
    'toolbar.removeStrikethrough': 'Remover riscado',
    'toolbar.bulletedList': 'Lista',
    'toolbar.numberedList': 'Lista numerada',
    'toolbar.checkList': 'Lista de verificação',
    'toolbar.link': 'Link',
    'toolbar.table': 'Tabela',
    'toolbar.thematicBreak': 'Linha divisória',
    'toolbar.blockTypeSelect.placeholder': 'Tipo de bloco',
    'toolbar.blockTypeSelect.selectBlockTypeTooltip': 'Tipo de bloco',
    'toolbar.blockTypes.paragraph': 'Parágrafo',
    'toolbar.blockTypes.quote': 'Citação',
    'toolbar.blockTypes.heading': 'Título {{level}}',
    'toolbar.richText': 'Texto',
    'toolbar.diffMode': 'Comparar',
    'toolbar.source': 'Markdown',
    'toolbar.editorType.rich-text': 'Texto',
    'toolbar.editorType.diff': 'Comparar',
    'toolbar.editorType.source': 'Markdown',
    'linkPreview.open': 'Abrir',
    'linkPreview.edit': 'Editar',
    'linkPreview.copyToClipboard': 'Copiar',
    'linkPreview.copied': 'Copiado',
    'linkPreview.remove': 'Remover',
    'createLink.url': 'Endereço',
    'createLink.title': 'Título',
    'createLink.saveTooltip': 'Salvar',
    'createLink.cancelTooltip': 'Cancelar',
    'table.deleteTable': 'Excluir tabela',
    'table.columnMenu': 'Coluna',
    'table.rowMenu': 'Linha',
    'table.insertRowAbove': 'Inserir linha acima',
    'table.insertRowBelow': 'Inserir linha abaixo',
    'table.deleteRow': 'Excluir linha',
    'table.insertColumnLeft': 'Inserir coluna à esquerda',
    'table.insertColumnRight': 'Inserir coluna à direita',
    'table.deleteColumn': 'Excluir coluna',
    'table.textAlignment': 'Alinhamento',
    'table.alignLeft': 'Esquerda',
    'table.alignCenter': 'Centro',
    'table.alignRight': 'Direita',
    'contentArea.editableMarkdown': 'markdown editável',
    'frontmatterEditor.title': 'Front matter',
    'frontmatterEditor.key': 'Chave',
    'frontmatterEditor.value': 'Valor',
    'frontmatterEditor.addEntry': 'Adicionar',
  }
  let text = dictionary[key] ?? defaultValue
  if (interpolations) {
    for (const [k, v] of Object.entries(interpolations)) text = text.replace(`{{${k}}}`, String(v))
  }
  return text
}
